import { authService } from "../services/authService";
import { walletService } from "../services/walletService";
import { meterService } from "../services/meterService";
import { billService } from "../services/billService";
import { transactionService } from "../services/transactionService";
import { estateService } from "../services/estateService";
import { appStore } from "../state/store";
import { can } from "../state/session";
import { estateContext } from "../state/estateContext";
import { byId, qsAll } from "./dom";
import { toKobo } from "./format";
import { renderTransactionList } from "./ledgerView";
import { initScanner } from "./scanner";
import { initUsageView } from "./usage";
import { initDashboard } from "./dashboard";
import { initBuyElectricityFlow, buyElectricityFlowRef } from "./buyElectricity";
import { initBillCentre } from "./billCentre";
import { initAccessCodeFlow } from "./accessCode";
import { initMetersList, initLinkMeterFlow } from "./meters";
import { initManageAdmins } from "./manageAdmins";
import { initEstates } from "./estates";
import { hideAuthShell, showAuthShell } from "./authView";
import { initBillLock, type BillLockController } from "./billLock";
import { initShop } from "./shop";

export interface AppShellController {
  boot(): void;
}

export function initAppShell(): AppShellController {
  const tabButtons = qsAll<HTMLButtonElement>(".tab-btn");
  const panels = qsAll<HTMLElement>(".tab-panel");

  const scanner = initScanner((serial) => {
    if (!can("meter:link")) return; // button is hidden for non-admins; this is the defense-in-depth backstop
    linkMeterFlow.open(serial);
  });

  let billLock: BillLockController | null = null;

  function activateTab(tabName: string): void {
    // A resident with an overdue bill can only be on the dashboard (where Pay Bills lives).
    if (billLock?.isLocked() && tabName !== "dashboard") return;
    tabButtons.forEach((btn) => btn.classList.toggle("is-active", btn.dataset.tab === tabName));
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== tabName;
    });
    if (tabName === "scan") {
      void scanner.start();
    } else {
      scanner.stop();
    }
  }

  tabButtons.forEach((btn) => {
    btn.addEventListener("click", () => activateTab(btn.dataset.tab as string));
  });

  qsAll<HTMLElement>("[data-tab-link]").forEach((el) => {
    el.addEventListener("click", () => activateTab(el.dataset.tabLink as string));
  });

  initUsageView();

  const dashboard = initDashboard();
  initBuyElectricityFlow();
  const billCentre = initBillCentre(() => activateTab("ledger"));
  billLock = initBillLock({
    openBills: () => billCentre.open(),
    showDashboard: () => activateTab("dashboard"),
    logout: async () => {
      await authService.logout();
      showAuthShell();
    },
  });
  const accessCodeFlow = initAccessCodeFlow();
  const linkMeterFlow = initLinkMeterFlow(async () => {
    await refreshMeters();
  });
  initMetersList(async () => {
    await refreshMeters();
  });
  const manageAdmins = initManageAdmins();
  const shop = initShop({ refreshWallet: () => { void loadActiveEstateData(); } });

  byId<HTMLButtonElement>("dashboard-link-meter-btn").addEventListener("click", () => linkMeterFlow.open());

  dashboard.onQuickAction("buy", () => activateTab("vend"));
  dashboard.onQuickAction("bills", () => billCentre.open());
  dashboard.onQuickAction("access", () => accessCodeFlow.open());
  buyElectricityFlowRef.setOnNeedFunds?.(() => {
    activateTab("dashboard");
    setTimeout(() => byId<HTMLButtonElement>("quick-action-fund").click(), 150);
  });

  /* -------------------------- Withdraw (admin) -------------------------- */

  const withdrawBtn = byId<HTMLButtonElement>("withdraw-wallet-btn");
  const withdrawForm = byId("wallet-withdraw-form");
  const withdrawAmountInput = byId<HTMLInputElement>("wallet-withdraw-amount");
  const withdrawSubmitBtn = byId<HTMLButtonElement>("wallet-withdraw-submit");
  const withdrawCancelBtn = byId("wallet-withdraw-cancel");
  const withdrawNote = byId("wallet-withdraw-note");
  const fundForm = byId("wallet-fund-form");

  function closeWithdrawForm(): void {
    withdrawForm.hidden = true;
    withdrawNote.hidden = true;
    withdrawAmountInput.value = "";
  }

  withdrawBtn.addEventListener("click", () => {
    if (!can("wallet:withdraw")) return;
    fundForm.hidden = true;
    const alreadyOpen = !withdrawForm.hidden;
    closeWithdrawForm();
    if (!alreadyOpen) {
      withdrawForm.hidden = false;
      withdrawAmountInput.focus();
    }
  });
  withdrawCancelBtn.addEventListener("click", closeWithdrawForm);

  withdrawSubmitBtn.addEventListener("click", async () => {
    if (!can("wallet:withdraw")) return;
    const naira = Number(withdrawAmountInput.value);
    if (!naira || naira < 100) {
      withdrawAmountInput.focus();
      return;
    }
    withdrawSubmitBtn.disabled = true;
    withdrawSubmitBtn.textContent = "Processing\u2026";
    withdrawNote.hidden = true;
    try {
      const result = await walletService.withdraw({ amountKobo: toKobo(naira) });
      if (result.status === "success" && result.newBalanceKobo !== undefined) {
        appStore.setWallet({
          estateId: appStore.state.wallet?.estateId ?? "",
          balanceKobo: result.newBalanceKobo,
          currency: "NGN",
          updatedAt: new Date().toISOString(),
        });
        appStore.prependTransaction({
          id: result.reference,
          type: "wallet_withdrawal",
          status: "success",
          amountKobo: result.amountKobo,
          direction: "debit",
          description: "Wallet Withdrawal",
          reference: result.reference,
          createdAt: new Date().toISOString(),
        });
        closeWithdrawForm();
      } else {
        withdrawNote.textContent = result.failureReason ?? "Withdrawal could not be completed.";
        withdrawNote.hidden = false;
      }
    } catch {
      withdrawNote.textContent = "Something went wrong. Please try again.";
      withdrawNote.hidden = false;
    } finally {
      withdrawSubmitBtn.disabled = false;
      withdrawSubmitBtn.textContent = "Withdraw";
    }
  });

  /* ------------------------------- Ledger ------------------------------- */

  const ledgerLogEl = byId("ledger-log");
  appStore.subscribe((state) => renderTransactionList(ledgerLogEl, state.recentTransactions, state.loading.transactions));

  /* -------------------------------- Hub -------------------------------- */

  qsAll<HTMLElement>(".hub-row[data-hub]").forEach((row) => {
    const key = row.dataset.hub;

    if (key === "fund-wallet") {
      row.addEventListener("click", () => {
        activateTab("dashboard");
        setTimeout(() => byId<HTMLButtonElement>("quick-action-fund").click(), 150);
      });
      return;
    }
    if (key === "bill-center") {
      row.addEventListener("click", () => billCentre.open());
      return;
    }
    if (key === "add-meter") {
      row.addEventListener("click", () => linkMeterFlow.open());
      return;
    }
    if (key === "facility-access") {
      row.addEventListener("click", () => accessCodeFlow.open());
      return;
    }
    if (key === "manage-admins") {
      row.addEventListener("click", () => manageAdmins.open());
      return;
    }
    if (key === "estates") {
      row.addEventListener("click", () => estates.open());
      return;
    }
    if (key === "logout") {
      row.addEventListener("click", async () => {
        const confirmed = window.confirm("Log out of PropertyVest?");
        if (!confirmed) return;
        await authService.logout();
        showAuthShell();
      });
      return;
    }

    const detail = document.getElementById(`hub-detail-${key}`);
    if (!detail) return;
    row.addEventListener("click", () => {
      const willOpen = !!detail.hidden;
      detail.hidden = !willOpen;
      row.classList.toggle("is-open", willOpen);
    });
  });

  /* ------------------------------- Boot data ------------------------------- */

  async function refreshMeters(): Promise<void> {
    appStore.setLoading("meters", true);
    try {
      const meters = await meterService.listMeters();
      appStore.setMeters(meters);
    } finally {
      appStore.setLoading("meters", false);
    }
  }

  /** Loads everything scoped to whichever estate is currently active
   *  (estateContext.activeEstateId) — called once at boot, and again
   *  whenever a super_admin switches estates, so the whole app actually
   *  reflects the estate now on screen instead of stale data from the
   *  previous one. */
  async function loadActiveEstateData(): Promise<void> {
    appStore.reset();
    appStore.setLoading("wallet", true);
    appStore.setLoading("meters", true);
    appStore.setLoading("bills", true);
    appStore.setLoading("transactions", true);

    // Issue any bills that have come due before we read the list.
    await billService.generateDue();

    const [wallet, meters, bills, txPage] = await Promise.all([
      walletService.getBalance(),
      meterService.listMeters(),
      billService.listBills(),
      transactionService.listTransactions({ limit: 20 }),
    ]);

    appStore.setWallet(wallet);
    appStore.setMeters(meters);
    appStore.setBills(bills);
    appStore.setRecentTransactions(txPage.items);

    appStore.setLoading("wallet", false);
    appStore.setLoading("meters", false);
    appStore.setLoading("bills", false);
    appStore.setLoading("transactions", false);

    void updateEstateContextUI();
  }

  /** Updates the header badge + the Hub "Estates" row's meta text with
   *  whichever estate is currently active. The header badge only shows
   *  when a super_admin has actually switched away from their own home
   *  estate — for everyone else (and a super_admin viewing their own
   *  estate) there's nothing ambiguous to flag. */
  async function updateEstateContextUI(): Promise<void> {
    const badge = byId("estate-context-badge");
    const hubMeta = document.getElementById("hub-current-estate-name");

    if (!estateContext.isOverridden) {
      badge.hidden = true;
      if (hubMeta) hubMeta.textContent = "";
      return;
    }

    try {
      const estate = await estateService.getCurrentEstate();
      badge.textContent = `Viewing: ${estate.name}`;
      badge.hidden = false;
      if (hubMeta) hubMeta.textContent = estate.name;
    } catch {
      badge.hidden = true;
    }
  }

  const estates = initEstates(() => {
    void loadActiveEstateData();
  });

  byId("estate-context-badge").addEventListener("click", () => estates.open());

  function boot(): void {
    hideAuthShell();
    activateTab("dashboard");

    // Role-dependent visibility that must be re-evaluated every login
    // (not just once at page load, before any session exists).
    const addMeterHubRow = document.querySelector<HTMLElement>('.hub-row[data-hub="add-meter"]');
    if (addMeterHubRow) addMeterHubRow.hidden = !can("meter:link");
    byId<HTMLButtonElement>("scanner-confirm-btn").hidden = !can("meter:link");
    byId("hub-section-platform").hidden = !can("admin:promote_users");

    void loadActiveEstateData();
    shop.check();
  }

  return { boot };
}
